'use client';

import { useState } from 'react';
import ToolHero from '@/components/tools/ToolHero';
import ToolCTA from '@/components/tools/ToolCTA';
import CopyButton from '@/components/tools/CopyButton';
import { RefreshCw, Clock, Mail, AlertCircle, TrendingUp, MessageSquare, Ban } from 'lucide-react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type Scenario =
  | 'After Interview'
  | 'After Application (No Response)'
  | 'After Networking Event'
  | 'After Job Fair'
  | 'After Referral'
  | 'Checking In After Offer'
  | 'After Rejection (Staying in Touch)';

type Timing =
  | '1-2 days'
  | '3-5 days'
  | '1 week'
  | '2 weeks'
  | '3+ weeks';

type Tone = 'Professional' | 'Friendly' | 'Persistent';

interface GeneratedEmail {
  subject: string;
  body: string;
  variationIndex: number;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const scenarios: Scenario[] = [
  'After Interview',
  'After Application (No Response)',
  'After Networking Event',
  'After Job Fair',
  'After Referral',
  'Checking In After Offer',
  'After Rejection (Staying in Touch)',
];

const timings: Timing[] = ['1-2 days', '3-5 days', '1 week', '2 weeks', '3+ weeks'];

const tones: Tone[] = ['Professional', 'Friendly', 'Persistent'];

// ---------------------------------------------------------------------------
// Timing-aware phrasing helpers
// ---------------------------------------------------------------------------

function timingPhrase(timing: Timing): string {
  switch (timing) {
    case '1-2 days':
      return 'recently';
    case '3-5 days':
      return 'earlier this week';
    case '1 week':
      return 'last week';
    case '2 weeks':
      return 'a couple of weeks ago';
    case '3+ weeks':
      return 'a few weeks ago';
  }
}

function timingOpener(timing: Timing): string {
  switch (timing) {
    case '1-2 days':
      return 'I wanted to follow up while our conversation is still fresh.';
    case '3-5 days':
      return 'I wanted to touch base following our recent conversation.';
    case '1 week':
      return 'It has been about a week since we last connected, and I wanted to follow up.';
    case '2 weeks':
      return 'I hope this message finds you well. I wanted to check in since it has been a couple of weeks since we last spoke.';
    case '3+ weeks':
      return 'I understand things can get busy, so I wanted to send a brief note to reconnect.';
  }
}

// ---------------------------------------------------------------------------
// Subject line templates (per scenario x tone = 21 combos, multiple each)
// ---------------------------------------------------------------------------

function getSubjectLines(
  scenario: Scenario,
  tone: Tone,
  jobTitle: string,
  company: string
): string[] {
  const job = jobTitle || '[Job Title]';
  const co = company || '[Company Name]';

  const map: Record<Scenario, Record<Tone, string[]>> = {
    'After Interview': {
      Professional: [
        `Following Up: ${job} Interview at ${co}`,
        `Checking In on ${job} Interview Status`,
        `Follow-Up Regarding the ${job} Position`,
        `${job} Interview Follow-Up - Thank You`,
        `Re: ${job} Position at ${co}`,
      ],
      Friendly: [
        `Great Meeting You - ${job} Role at ${co}`,
        `Touching Base After Our ${job} Chat`,
        `Following Up on Our ${job} Conversation`,
        `Loved Chatting About the ${job} Role`,
        `Quick Follow-Up: ${job} at ${co}`,
      ],
      Persistent: [
        `Still Very Interested in the ${job} Position`,
        `Circling Back: ${job} Interview at ${co}`,
        `Checking In Again - ${job} Opportunity`,
        `Following Up Once More: ${job} at ${co}`,
        `Eager to Hear Back - ${job} Position`,
      ],
    },
    'After Application (No Response)': {
      Professional: [
        `Following Up on My Application: ${job} at ${co}`,
        `Application Status Inquiry - ${job}`,
        `Checking In: ${job} Application at ${co}`,
        `Re: My Application for the ${job} Position`,
        `${job} Application Follow-Up`,
      ],
      Friendly: [
        `Excited About the ${job} Role - Any Updates?`,
        `Quick Check-In on My ${job} Application`,
        `Still Thinking About the ${job} Opportunity`,
        `Hi from a ${job} Applicant at ${co}`,
        `Hoping to Connect About the ${job} Position`,
      ],
      Persistent: [
        `Following Up Again: ${job} Application at ${co}`,
        `Reaffirming My Interest in the ${job} Role`,
        `Still Eager for the ${job} Position at ${co}`,
        `Circling Back on My ${job} Application`,
        `One More Note: ${job} Application at ${co}`,
      ],
    },
    'After Networking Event': {
      Professional: [
        `Great Connecting at [Event Name] - Following Up`,
        `Following Up From Our Conversation at [Event]`,
        `Enjoyed Meeting You - Professional Follow-Up`,
        `Post-Event Follow-Up: Exploring Opportunities at ${co}`,
        `Connecting After [Event Name]`,
      ],
      Friendly: [
        `So Glad We Met at [Event Name]!`,
        `Loved Our Chat at [Event] - Let's Stay in Touch`,
        `Following Up After a Great Conversation`,
        `It Was a Pleasure Meeting You at [Event]`,
        `Quick Hello After [Event Name]`,
      ],
      Persistent: [
        `Circling Back After [Event Name]`,
        `Reconnecting - We Met at [Event Name]`,
        `Following Up Once More From [Event]`,
        `Still Hoping to Connect After [Event Name]`,
        `Reaching Out Again After [Event]`,
      ],
    },
    'After Job Fair': {
      Professional: [
        `Following Up From the Job Fair - ${job} at ${co}`,
        `Enjoyed Speaking With You at the Job Fair`,
        `Post-Job Fair Follow-Up: ${job} Position`,
        `Job Fair Follow-Up - Interest in ${co}`,
        `Connecting After the Career Fair - ${job}`,
      ],
      Friendly: [
        `Great Chatting at the Job Fair - ${co}`,
        `Following Up on Our Job Fair Conversation`,
        `Excited After Meeting ${co} at the Job Fair`,
        `Hello From the Job Fair - Interested in ${job}`,
        `Loved Learning About ${co} at the Career Fair`,
      ],
      Persistent: [
        `Circling Back: Job Fair Conversation About ${job}`,
        `Still Interested After the Job Fair - ${co}`,
        `Following Up Again From the Career Fair`,
        `Reaffirming Interest: ${job} at ${co}`,
        `One More Note After the Job Fair`,
      ],
    },
    'After Referral': {
      Professional: [
        `Referred by [Referrer Name] - ${job} at ${co}`,
        `Following Up on Referral for ${job} Position`,
        `Introduction via [Referrer Name] - ${job} Role`,
        `Referral Follow-Up: ${job} at ${co}`,
        `Checking In on Referred Application - ${job}`,
      ],
      Friendly: [
        `[Referrer Name] Suggested I Reach Out - ${job}`,
        `Following Up on a Warm Introduction - ${co}`,
        `Connected Through [Referrer Name] - Quick Follow-Up`,
        `Hi! [Referrer Name] Mentioned the ${job} Role`,
        `Touching Base After [Referrer Name]'s Referral`,
      ],
      Persistent: [
        `Following Up Again: Referral for ${job} at ${co}`,
        `Circling Back on Referral - ${job} Position`,
        `Still Excited About the ${job} Role (Referral)`,
        `Reconnecting: Referred for ${job} at ${co}`,
        `Checking In Once More - Referral for ${job}`,
      ],
    },
    'Checking In After Offer': {
      Professional: [
        `Following Up on the ${job} Offer at ${co}`,
        `Checking In: ${job} Offer Details`,
        `Re: ${job} Offer - Next Steps`,
        `Offer Follow-Up for ${job} Position`,
        `${job} Offer Status at ${co}`,
      ],
      Friendly: [
        `Excited About the ${job} Offer - Quick Question`,
        `Touching Base on the ${job} Offer at ${co}`,
        `Following Up on an Exciting Opportunity - ${job}`,
        `Looking Forward to Joining ${co} - Quick Follow-Up`,
        `Can't Wait to Get Started - ${job} Offer`,
      ],
      Persistent: [
        `Circling Back on the ${job} Offer`,
        `Hoping to Finalize: ${job} Offer at ${co}`,
        `Following Up Again - ${job} Offer Details`,
        `Checking In Once More: ${job} at ${co}`,
        `Eager to Move Forward - ${job} Offer`,
      ],
    },
    'After Rejection (Staying in Touch)': {
      Professional: [
        `Thank You for the Update - Staying Connected`,
        `Appreciated the Opportunity - ${job} at ${co}`,
        `Thank You and Future Opportunities at ${co}`,
        `Grateful for the ${job} Interview Experience`,
        `Staying in Touch - ${co}`,
      ],
      Friendly: [
        `Thanks for Letting Me Know - Let's Keep in Touch`,
        `No Hard Feelings - Would Love to Stay Connected`,
        `Grateful for the Experience - Hope to Connect Again`,
        `Thank You! I'd Love to Stay on Your Radar`,
        `Appreciated the Chance - Looking Forward to the Future`,
      ],
      Persistent: [
        `Keeping the Door Open - Future Roles at ${co}`,
        `Still a Big Fan of ${co} - Please Keep Me in Mind`,
        `Would Love Another Shot at ${co} in the Future`,
        `Staying on Your Radar for Future Openings`,
        `Hoping for Another Opportunity at ${co}`,
      ],
    },
  };

  return map[scenario][tone];
}

// ---------------------------------------------------------------------------
// Body templates
// ---------------------------------------------------------------------------

function getBodyTemplates(
  scenario: Scenario,
  tone: Tone,
  timing: Timing,
  contactName: string,
  contactTitle: string,
  company: string,
  jobTitle: string,
  additionalContext: string
): string[] {
  const name = contactName || '[Contact Name]';
  const title = contactTitle || '[Their Title]';
  const co = company || '[Company Name]';
  const job = jobTitle || '[Job Title]';
  const timePhrase = timingPhrase(timing);
  const opener = timingOpener(timing);
  const contextSentence = additionalContext
    ? `\n\n${additionalContext}`
    : '';

  // ----- After Interview -----
  if (scenario === 'After Interview') {
    if (tone === 'Professional') {
      return [
`Dear ${name},

Thank you for taking the time to meet with me ${timePhrase} to discuss the ${job} position at ${co}. ${opener}

I remain very interested in the opportunity and believe my skills and experience align well with what the team is looking for. I was particularly impressed by the work ${co} is doing and would welcome the chance to contribute.${contextSentence}

I would appreciate any updates you can share regarding the timeline for next steps. Please do not hesitate to reach out if you need any additional information from me.

Best regards,
[Your Name]`,

`Dear ${name},

I hope this message finds you well. ${opener} I wanted to reiterate my strong interest in the ${job} role at ${co}.

Our conversation reinforced my belief that this position is an excellent match for my background. I am confident that I could make a meaningful impact on your team.${contextSentence}

If there is any additional information I can provide to support the decision-making process, I am happy to do so. I look forward to hearing from you.

Sincerely,
[Your Name]`,

`Dear ${name},

${opener} I wanted to express my continued enthusiasm for the ${job} opportunity at ${co}.

Speaking with you about the role and the team's goals gave me a much clearer picture of how I could contribute. I am eager to bring my expertise to ${co} and help drive results.${contextSentence}

Please let me know if there are any updates on the hiring timeline or if there is anything else I can provide. Thank you again for your time and consideration.

With appreciation,
[Your Name]`,

`Dear ${name},

Thank you once again for the opportunity to interview for the ${job} position at ${co} ${timePhrase}. ${opener}

I left our meeting feeling very positive about the role and the direction of the team. My experience in this area positions me well to contribute immediately and I am excited about the possibility.${contextSentence}

I would be grateful for any updates you can share. I remain enthusiastic about joining ${co} and am available should you need anything further from me.

Respectfully,
[Your Name]`,

`Dear ${name},

${opener} I continue to be very interested in the ${job} role at ${co} and wanted to check in regarding the status of the hiring process.

I greatly enjoyed learning about the team's work and am confident I would be a strong addition. The challenges and opportunities you described are exactly what I am looking for in my next role.${contextSentence}

Thank you for keeping me informed. I look forward to hearing from you at your earliest convenience.

Kind regards,
[Your Name]`,
      ];
    }

    if (tone === 'Friendly') {
      return [
`Hi ${name},

I hope you are doing well! ${opener} I had such a great time chatting about the ${job} role at ${co} and wanted to touch base.

I have been thinking a lot about our conversation and I am even more excited about the possibility of joining the team. Everything you shared about ${co}'s culture and vision really resonated with me.${contextSentence}

Any updates on next steps would be wonderful. In the meantime, feel free to reach out if I can provide anything else.

Thanks so much,
[Your Name]`,

`Hi ${name},

Just wanted to drop you a quick note to say how much I enjoyed our conversation about the ${job} position ${timePhrase}. ${opener}

I left feeling genuinely energized about the role and ${co}. It sounds like exactly the kind of environment where I would thrive.${contextSentence}

I would love to hear how things are progressing on your end. Thanks again for such a great meeting!

All the best,
[Your Name]`,

`Hi ${name},

${opener} I really enjoyed meeting you and learning more about the ${job} opportunity at ${co}.

I keep coming back to how well this role aligns with what I am looking for. The projects and challenges you described are right up my alley, and I would love the chance to be part of it.${contextSentence}

Let me know if there is anything else you need from me. Looking forward to hearing from you!

Cheers,
[Your Name]`,

`Hi ${name},

Hope your week is going well! ${opener} Our conversation about the ${job} role at ${co} was a real highlight for me.

I have been reflecting on everything we discussed and my enthusiasm has only grown. I genuinely think this could be a fantastic fit on both sides.${contextSentence}

Would love to hear any updates when you get a chance. Thanks for being so generous with your time!

Warmly,
[Your Name]`,

`Hi ${name},

${opener} I wanted to say thanks again for the great conversation about the ${job} role. Meeting you and learning about the team at ${co} really made my day.

I am still very much interested and excited about the possibility. The more I think about it, the more I can picture myself contributing to what you are building.${contextSentence}

Any news on next steps? I am all ears. Thanks again!

Take care,
[Your Name]`,
      ];
    }

    // Persistent
    return [
`Dear ${name},

I hope this message finds you well. ${opener} I wanted to reach out once more regarding the ${job} position at ${co}.

I understand that hiring decisions take time, and I want you to know that my interest in this role has not waned. I remain confident that my background and skills make me a strong candidate for your team.${contextSentence}

I would genuinely appreciate any update you can share, even a brief note on the timeline. I am happy to provide any additional materials or references that might be helpful.

Thank you for your time and consideration.

Best regards,
[Your Name]`,

`Dear ${name},

${opener} I wanted to circle back about the ${job} opportunity at ${co}. I realize you likely have many candidates to evaluate, but I wanted to reaffirm that this role is my top choice.

My enthusiasm for the position and for ${co}'s mission has only grown since we spoke. I am ready and eager to contribute.${contextSentence}

If there is anything I can do to move the process forward, please let me know. I appreciate your time and look forward to your response.

Sincerely,
[Your Name]`,

`Dear ${name},

I wanted to follow up one more time on the ${job} position at ${co}. ${opener}

I completely understand that these processes can take longer than expected. I want to reiterate that I am very serious about this opportunity and confident I can deliver strong results for your team.${contextSentence}

If the timing is not right or if the role has been filled, I completely understand -- I would just appreciate knowing so I can plan accordingly. Thank you for your consideration.

Respectfully,
[Your Name]`,

`Dear ${name},

${opener} I have been thinking a great deal about the ${job} role and I wanted to check in one more time.

I do not want to be a nuisance, but I also do not want to miss out on what I believe is an outstanding opportunity. My skills align strongly with what you are looking for, and I am ready to hit the ground running.${contextSentence}

A quick update on where things stand would mean a lot. Thank you for bearing with me, and I appreciate all of the time you have invested.

Kind regards,
[Your Name]`,

`Dear ${name},

I hope you do not mind me reaching out again. ${opener} The ${job} position at ${co} remains at the top of my list.

I know how hectic the hiring process can be, so I wanted to gently remind you of my strong interest. I have given a lot of thought to how I can contribute to the team and I am more excited than ever.${contextSentence}

I would be grateful for any update, no matter how brief. Thank you for your patience and your time.

With appreciation,
[Your Name]`,
    ];
  }

  // ----- After Application (No Response) -----
  if (scenario === 'After Application (No Response)') {
    if (tone === 'Professional') {
      return [
`Dear ${name},

I recently submitted my application for the ${job} position at ${co} and wanted to follow up to confirm it was received. ${opener}

I am very interested in this opportunity and believe my background aligns well with the requirements of the role. I would welcome the chance to discuss how my experience could benefit your team.${contextSentence}

I understand you may be reviewing many applications. If there is any additional information I can provide, please do not hesitate to let me know.

Thank you for your time and consideration.

Best regards,
[Your Name]`,

`Dear ${name},

${opener} I am writing to follow up on my application for the ${job} role at ${co}.

Having researched ${co}'s work extensively, I am confident that my skills and experience would make me a strong contributor to your team. I am particularly drawn to the mission and direction of the organization.${contextSentence}

I would appreciate any updates regarding the status of my application and would be happy to provide additional materials if needed.

Sincerely,
[Your Name]`,

`Dear ${name},

I hope this message finds you well. I applied for the ${job} position at ${co} ${timePhrase} and wanted to check in on the status of my application.

The role is an excellent fit for my background, and I am genuinely enthusiastic about the opportunity to contribute to ${co}.${contextSentence}

Please let me know if there is anything I can do to support the review process. I look forward to hearing from you.

With appreciation,
[Your Name]`,

`Dear Hiring Team,

${opener} I submitted my application for the ${job} position at ${co} and wanted to express my continued interest.

I believe I bring a strong combination of skills and experience that align with what you are looking for. I would be thrilled to have the chance to discuss the role further.${contextSentence}

Thank you for considering my application. I hope to hear from you soon.

Respectfully,
[Your Name]`,

`Dear ${name},

I am following up on my application for the ${job} role at ${co}, which I submitted ${timePhrase}. ${opener}

I remain very interested in this position and am confident that I would be a valuable addition to your team. I am happy to provide references, work samples, or any other materials that might be helpful.${contextSentence}

Thank you for your time. I look forward to any updates.

Kind regards,
[Your Name]`,
      ];
    }

    if (tone === 'Friendly') {
      return [
`Hi ${name},

I hope you are having a great week! I applied for the ${job} position at ${co} ${timePhrase} and just wanted to quickly check in. ${opener}

I have been following ${co}'s work and I am really excited about the possibility of joining the team. I think my background would be a great fit!${contextSentence}

No rush at all -- I know you must be busy. Just wanted to make sure my application made it through. Thanks so much!

All the best,
[Your Name]`,

`Hi ${name},

${opener} I submitted my application for the ${job} role at ${co} and wanted to drop a quick note to say how excited I am about it.

I have done a lot of research on ${co} and the more I learn, the more I want to be part of what you are building. I think I could bring a lot to the table.${contextSentence}

Any updates would be greatly appreciated, but I understand if you are still working through things. Thanks for your time!

Cheers,
[Your Name]`,

`Hi there,

Just a friendly follow-up on my application for the ${job} role at ${co}! ${opener}

I know hiring can take time, so no pressure at all. I just wanted to reiterate that I am genuinely excited about this opportunity and would love the chance to chat if it makes sense.${contextSentence}

Thanks so much for considering me. Hope to hear from you!

Warmly,
[Your Name]`,

`Hi ${name},

Hope this finds you well! ${opener} I wanted to touch base about my application for the ${job} position at ${co}.

I am still very much interested and would love to learn more about the team and the role. If there is a good time to connect, I am flexible and happy to work around your schedule.${contextSentence}

Thanks for your time -- looking forward to hearing from you!

Take care,
[Your Name]`,

`Hi ${name},

${opener} I applied for the ${job} position ${timePhrase} and have been eagerly waiting to hear back.

I am really drawn to what ${co} is doing and think this role would be a wonderful fit for my skills and interests. Would love the chance to tell you more about what I could bring.${contextSentence}

Let me know if there is anything else I can share. Thanks a bunch!

Best,
[Your Name]`,
      ];
    }

    // Persistent
    return [
`Dear ${name},

${opener} I submitted my application for the ${job} role at ${co} ${timePhrase} and wanted to follow up once more.

I understand the hiring process can be time-consuming, and I do not want to be a bother. However, I want to make sure my strong interest in this position is clear. I believe I am a compelling candidate and would appreciate the opportunity to discuss the role.${contextSentence}

Even a brief update on where things stand would be very helpful. Thank you for your patience and consideration.

Best regards,
[Your Name]`,

`Dear ${name},

I am reaching out again to express my continued interest in the ${job} position at ${co}. ${opener}

I have given a great deal of thought to this role and remain convinced it is an excellent match for my skills and career goals. I am ready to contribute from day one.${contextSentence}

If the position has been filled or the timeline has shifted, I completely understand and would appreciate knowing. Thank you for your time.

Sincerely,
[Your Name]`,

`Dear ${name},

${opener} I want to be transparent -- the ${job} role at ${co} is my top choice, which is why I am following up again.

I believe strongly in what ${co} is building and am confident I can make a real impact. I would be grateful for any update on the status of my application, however brief.${contextSentence}

Thank you for considering me. I hope to hear from you soon.

Respectfully,
[Your Name]`,

`Dear Hiring Team,

I am writing one more time to follow up on my application for the ${job} position at ${co}. ${opener}

My interest in this role is genuine and strong. I have continued to prepare and research the position, and I am more enthusiastic than ever about the prospect of joining your team.${contextSentence}

I appreciate your time and look forward to any news you can share. Thank you.

Kind regards,
[Your Name]`,

`Dear ${name},

${opener} I wanted to reach out once more about the ${job} position at ${co}. I know I have been persistent, and I hope you will take that as a sign of how seriously I take this opportunity.

I would welcome even a brief update on the timeline. If the role has been filled, I would love to be considered for future openings.${contextSentence}

Thank you for your understanding and for the time you have spent reviewing applications.

With appreciation,
[Your Name]`,
    ];
  }

  // ----- After Networking Event -----
  if (scenario === 'After Networking Event') {
    if (tone === 'Professional') {
      return [
`Dear ${name},

It was a pleasure meeting you ${timePhrase} at [Event Name]. ${opener}

I enjoyed our conversation about your work at ${co}, particularly your role as ${title}. I am very interested in learning more about opportunities on your team and how my background might be a good fit.${contextSentence}

I would welcome the chance to continue our conversation over coffee or a phone call. Please let me know if that would be possible.

Best regards,
[Your Name]`,

`Dear ${name},

${opener} I wanted to reach out and say how much I appreciated our conversation at [Event Name] ${timePhrase}.

Learning about your work as ${title} at ${co} was genuinely inspiring. I believe there may be strong alignment between my experience and the work your team is doing.${contextSentence}

Would you be open to a brief call or meeting to explore this further? I would be grateful for the opportunity.

Sincerely,
[Your Name]`,

`Dear ${name},

Thank you for taking the time to speak with me at [Event Name] ${timePhrase}. ${opener}

Your insights into ${co}'s direction were valuable, and I came away with a strong interest in exploring how I might contribute. I believe my background would complement the work your team is doing.${contextSentence}

I would appreciate the opportunity to continue our discussion. Please feel free to reach out at your convenience.

With appreciation,
[Your Name]`,
      ];
    }

    if (tone === 'Friendly') {
      return [
`Hi ${name},

So glad we had the chance to chat at [Event Name] ${timePhrase}! ${opener}

I really enjoyed hearing about what you and the team at ${co} are up to. It sounds like you are doing some amazing work, and I would love to learn more.${contextSentence}

Would you be up for a quick coffee or phone call sometime? No pressure at all -- just thought it would be great to stay connected.

Thanks and talk soon!
[Your Name]`,

`Hi ${name},

${opener} Meeting you at [Event Name] was definitely a highlight! I loved our conversation about ${co} and the exciting things happening there.

I think there could be some great overlap between what I do and what your team is working on. Would be awesome to explore that further.${contextSentence}

Let me know if you ever have time for a quick chat. Would love to keep in touch!

All the best,
[Your Name]`,

`Hi ${name},

Wanted to send a quick note to say thanks for the great conversation at [Event Name]! ${opener}

Hearing about your role as ${title} at ${co} was really inspiring. I have been thinking about our chat ever since.${contextSentence}

Let's definitely stay in touch. I would love to grab coffee or hop on a call sometime if you are up for it.

Cheers,
[Your Name]`,
      ];
    }

    // Persistent
    return [
`Dear ${name},

${opener} I am reaching out again following our conversation at [Event Name] ${timePhrase}. I understand how busy things can get, but I wanted to make sure my note did not get lost.

I remain very interested in learning more about the work at ${co} and exploring potential opportunities. Your insights as ${title} left a strong impression on me.${contextSentence}

I would love to schedule a brief conversation at your convenience. Thank you for your time.

Best regards,
[Your Name]`,

`Dear ${name},

I wanted to follow up once more on our conversation at [Event Name]. ${opener}

I do not want to be overbearing, but I was genuinely excited by what you shared about ${co} and I would value the chance to explore how my skills could contribute.${contextSentence}

Even a brief chat would be greatly appreciated. Thank you for your patience.

Sincerely,
[Your Name]`,

`Dear ${name},

${opener} I hope this follow-up finds you well. We connected at [Event Name] ${timePhrase} and I have been keen to continue our discussion.

I believe there is real potential for collaboration or contribution on my part, and I would be grateful for even a few minutes of your time.${contextSentence}

Thank you again for the initial conversation. I look forward to hearing from you.

Kind regards,
[Your Name]`,
    ];
  }

  // ----- After Job Fair -----
  if (scenario === 'After Job Fair') {
    if (tone === 'Professional') {
      return [
`Dear ${name},

Thank you for speaking with me at the career fair ${timePhrase}. ${opener}

I was impressed by what you shared about ${co} and the ${job} opportunity. After our conversation, I am even more confident that my qualifications align with what you are looking for.${contextSentence}

I have submitted my application and would welcome the chance to discuss the role in more detail. Please let me know if there is anything else I can provide.

Best regards,
[Your Name]`,

`Dear ${name},

${opener} I enjoyed learning about ${co} at the career fair and am writing to express my continued interest in the ${job} position.

The work your team is doing aligns closely with my skills and career goals. I would be grateful for the opportunity to take the next step in the application process.${contextSentence}

Thank you for your time, both at the fair and in reviewing this message.

Sincerely,
[Your Name]`,

`Dear ${name},

It was a pleasure connecting with you at the career fair ${timePhrase}. ${opener}

Our conversation about the ${job} role at ${co} confirmed that this opportunity is an excellent fit for my background. I am eager to move forward and learn more about the team and the work ahead.${contextSentence}

I look forward to hearing from you. Thank you for your consideration.

With appreciation,
[Your Name]`,
      ];
    }

    if (tone === 'Friendly') {
      return [
`Hi ${name},

I had such a great time chatting with you at the career fair ${timePhrase}! ${opener}

I was really excited to hear about the ${job} role and what the team at ${co} is working on. It sounds like an incredible place to work, and I would love to learn more.${contextSentence}

Let me know if there is a good time to connect further. Thanks again for such a fun conversation!

All the best,
[Your Name]`,

`Hi ${name},

${opener} Wanted to say thanks for making the career fair such a positive experience! Your enthusiasm for ${co} was contagious.

I am really interested in the ${job} position and think I would be a great fit. Would love to hear more about next steps!${contextSentence}

Thanks and hope to chat again soon!

Cheers,
[Your Name]`,

`Hi ${name},

The career fair was great, and meeting you was the best part! ${opener}

I cannot stop thinking about the ${job} opportunity at ${co}. Everything you described sounds like it was made for someone with my background.${contextSentence}

Would love to continue the conversation. Thanks for being so welcoming!

Warmly,
[Your Name]`,
      ];
    }

    // Persistent
    return [
`Dear ${name},

${opener} I wanted to follow up once more since we spoke at the career fair ${timePhrase} about the ${job} position at ${co}.

I know you met many candidates, but I want to reiterate my strong interest and ensure my name is still in consideration. I believe I have a lot to offer the team.${contextSentence}

Thank you for your time, and I hope to hear from you soon.

Best regards,
[Your Name]`,

`Dear ${name},

I am circling back regarding the ${job} role we discussed at the career fair. ${opener}

I remain very enthusiastic about this opportunity and would appreciate any update you can provide on the hiring process. I am ready and eager to contribute.${contextSentence}

Thank you for your patience and consideration.

Sincerely,
[Your Name]`,

`Dear ${name},

${opener} I wanted to reach out again about the ${job} position at ${co} that we discussed at the career fair ${timePhrase}.

My interest has only grown since we spoke, and I want to make sure I have done everything I can to move forward. Please let me know if there are any additional steps I should take.${contextSentence}

I appreciate your time and look forward to your reply.

Kind regards,
[Your Name]`,
    ];
  }

  // ----- After Referral -----
  if (scenario === 'After Referral') {
    if (tone === 'Professional') {
      return [
`Dear ${name},

I was referred to you by [Referrer Name] regarding the ${job} position at ${co}. ${opener}

I submitted my application ${timePhrase} and wanted to follow up to express my strong interest. [Referrer Name] spoke very highly of the team and the work being done at ${co}, which made me even more enthusiastic about the opportunity.${contextSentence}

I would appreciate any updates on the status of my application and welcome the chance to discuss how I could contribute to your team.

Best regards,
[Your Name]`,

`Dear ${name},

${opener} I am following up on my application for the ${job} role at ${co}. I was connected to this opportunity through [Referrer Name], who suggested I would be a strong fit.

I believe my skills and experience align well with the role's requirements, and I am eager to contribute to ${co}'s success.${contextSentence}

Please let me know if there is any additional information I can provide. I look forward to hearing from you.

Sincerely,
[Your Name]`,

`Dear ${name},

Thank you for considering my application for the ${job} position at ${co}. ${opener} I was introduced to this opportunity by [Referrer Name], and the more I have learned about the role, the more excited I have become.

I am confident that my background would be a valuable asset to your team and I would welcome the chance to discuss the position further.${contextSentence}

I appreciate your time and consideration.

With appreciation,
[Your Name]`,
      ];
    }

    if (tone === 'Friendly') {
      return [
`Hi ${name},

[Referrer Name] suggested I get in touch about the ${job} role at ${co}, and I just wanted to follow up! ${opener}

I applied ${timePhrase} and I am really excited about this opportunity. [Referrer Name] had such great things to say about working with you and the team, and that made me even more interested.${contextSentence}

I would love the chance to chat if you have a few minutes. Thanks so much!

All the best,
[Your Name]`,

`Hi ${name},

${opener} Wanted to check in on my application for the ${job} position at ${co}. [Referrer Name] connected us, and I am so glad they did!

Everything I have learned about ${co} makes me think this would be a fantastic fit. I would love to tell you more about what I can bring to the table.${contextSentence}

Let me know if there is a good time to connect. Thanks!

Cheers,
[Your Name]`,

`Hi ${name},

Hope you are doing well! ${opener} [Referrer Name] put me in touch about the ${job} role and I wanted to follow up since I applied ${timePhrase}.

I have been really impressed by what I have learned about ${co} and am excited about the possibility of joining the team.${contextSentence}

Any updates would be awesome. Thanks for your time!

Warmly,
[Your Name]`,
      ];
    }

    // Persistent
    return [
`Dear ${name},

${opener} I am following up once more on my referral from [Referrer Name] for the ${job} position at ${co}.

I understand the hiring process has many moving parts, and I want to be respectful of your time. However, I also want to make it clear that this role is my top priority. My background strongly aligns with what you are looking for.${contextSentence}

I would be grateful for any update, even a brief one. Thank you for your consideration.

Best regards,
[Your Name]`,

`Dear ${name},

I wanted to reach out again regarding the ${job} role at ${co}. ${opener} [Referrer Name] connected us, and I remain very eager to contribute to your team.

I know I have followed up before, and I appreciate your patience. This opportunity means a great deal to me, and I want to make sure I have done everything possible.${contextSentence}

Thank you for your time. I look forward to hearing from you.

Sincerely,
[Your Name]`,

`Dear ${name},

${opener} I am circling back one more time about the ${job} position at ${co}, for which I was referred by [Referrer Name].

I continue to be very enthusiastic about this opportunity. If there have been any changes in the timeline or process, I would appreciate being informed. I am flexible and ready to proceed whenever the time is right.${contextSentence}

Thank you for bearing with me, and thank you for the opportunity.

Kind regards,
[Your Name]`,
    ];
  }

  // ----- Checking In After Offer -----
  if (scenario === 'Checking In After Offer') {
    if (tone === 'Professional') {
      return [
`Dear ${name},

Thank you again for extending the offer for the ${job} position at ${co}. ${opener}

I am very excited about the opportunity and wanted to check in regarding a few details. I want to make sure I have everything I need to make a well-informed decision and proceed smoothly.${contextSentence}

Could we schedule a brief call to discuss any remaining details? I am available at your convenience.

Best regards,
[Your Name]`,

`Dear ${name},

${opener} I wanted to follow up on the ${job} offer from ${co}. I am genuinely enthusiastic about joining the team and am in the process of finalizing my decision.

If there are any documents, timelines, or details you need from me, please let me know. I want to ensure a smooth transition.${contextSentence}

Thank you for your patience and support throughout this process.

Sincerely,
[Your Name]`,

`Dear ${name},

I am writing to follow up on the ${job} offer at ${co}. ${opener}

I have given the offer careful consideration and remain very positive about the opportunity. I wanted to check if there are any upcoming deadlines or next steps I should be aware of.${contextSentence}

I appreciate your time and look forward to wrapping everything up.

With appreciation,
[Your Name]`,
      ];
    }

    if (tone === 'Friendly') {
      return [
`Hi ${name},

I hope you are doing well! ${opener} I wanted to check in on the ${job} offer at ${co}.

I am thrilled about the opportunity and just want to make sure I have all my ducks in a row before everything is finalized. Is there anything you need from me at this point?${contextSentence}

Thanks so much for everything -- really looking forward to this next chapter!

All the best,
[Your Name]`,

`Hi ${name},

${opener} Just wanted to touch base about the ${job} offer. I am so excited about joining ${co} and can hardly wait to get started!

If there are any next steps or details to iron out, I am ready and happy to help move things along.${contextSentence}

Thanks for being so wonderful throughout this process!

Cheers,
[Your Name]`,

`Hi ${name},

Quick follow-up on the ${job} offer from ${co}! ${opener}

I have been looking forward to this opportunity and just want to make sure everything is on track. If there is anything I can do on my end to speed things up, just say the word.${contextSentence}

Thanks again -- I really appreciate it!

Warmly,
[Your Name]`,
      ];
    }

    // Persistent
    return [
`Dear ${name},

${opener} I am following up on the ${job} offer from ${co}. I want to reiterate how excited I am about this opportunity and am eager to finalize the details.

I understand there may be internal processes at play, but I wanted to check in on the timeline so I can plan accordingly.${contextSentence}

Thank you for your continued support. I look forward to your response.

Best regards,
[Your Name]`,

`Dear ${name},

I wanted to reach out again regarding the ${job} offer at ${co}. ${opener}

I remain fully committed to joining the team and want to ensure nothing falls through the cracks. If there are any outstanding items on your end, I am happy to help address them.${contextSentence}

I appreciate your patience and look forward to getting everything wrapped up.

Sincerely,
[Your Name]`,

`Dear ${name},

${opener} I am checking in once more on the ${job} offer from ${co}. I hope I am not being overly eager, but I want to make sure we are aligned on timing and next steps.

This is a move I am very excited about, and I want to do everything I can to make a smooth start possible.${contextSentence}

Thank you for your understanding. I look forward to hearing from you soon.

Kind regards,
[Your Name]`,
    ];
  }

  // ----- After Rejection (Staying in Touch) -----
  if (scenario === 'After Rejection (Staying in Touch)') {
    if (tone === 'Professional') {
      return [
`Dear ${name},

Thank you for informing me about the decision regarding the ${job} position at ${co}. While I am disappointed, I sincerely appreciate the time and consideration you and the team invested in my candidacy.

I remain very impressed by ${co}'s work and culture. If a similar opportunity arises in the future, I would be grateful to be considered again.${contextSentence}

Thank you for the positive experience throughout the process. I wish you and the team continued success.

Best regards,
[Your Name]`,

`Dear ${name},

I appreciate you sharing the outcome for the ${job} role at ${co}. Although the news is not what I hoped for, I understand and respect the decision.

I would love to stay on your radar for future opportunities. My admiration for ${co} is genuine, and I believe the right role may come along.${contextSentence}

Thank you again for the opportunity. Please do not hesitate to reach out if circumstances change.

Sincerely,
[Your Name]`,

`Dear ${name},

Thank you for the update on the ${job} position at ${co}. I am grateful for the opportunity to have interviewed and learned more about the team.

I would welcome the chance to stay connected and be considered for future openings. ${co}'s mission resonates deeply with me, and I hope our paths will cross again.${contextSentence}

Wishing you and the team all the best.

With appreciation,
[Your Name]`,
      ];
    }

    if (tone === 'Friendly') {
      return [
`Hi ${name},

Thanks for letting me know about the ${job} decision. I am not going to lie -- I am a little bummed! But I completely understand, and I really appreciated getting to know you and the team at ${co} through this process.

I would love to stay in touch. If anything else comes up that might be a fit, I would jump at the chance to chat again.${contextSentence}

Wishing you and the team nothing but great things ahead!

All the best,
[Your Name]`,

`Hi ${name},

Thank you for the update on the ${job} role. While it was not the outcome I was hoping for, I genuinely enjoyed the experience and meeting everyone at ${co}.

Let's definitely stay connected! I would love to hear about future opportunities or even just keep up with what the team is doing.${contextSentence}

Thanks again for everything. Rooting for you all!

Take care,
[Your Name]`,

`Hi ${name},

Got your note about the ${job} position -- thanks for being so thoughtful in sharing the news. These things happen, and I have nothing but good feelings about my experience with ${co}.

I would love to keep the door open. You never know what the future might bring, right?${contextSentence}

Thanks for being so gracious throughout the process. Stay in touch!

Cheers,
[Your Name]`,
      ];
    }

    // Persistent
    return [
`Dear ${name},

Thank you for the update on the ${job} position at ${co}. I respect the decision and appreciate the transparency.

I want you to know that my interest in ${co} extends beyond this single role. I genuinely believe in what you are building and would welcome the opportunity to contribute in the future. I plan to keep an eye on your openings and would be grateful if you would keep me in mind as well.${contextSentence}

Thank you for the experience. I look forward to staying connected.

Best regards,
[Your Name]`,

`Dear ${name},

While I am disappointed to learn the ${job} role has gone in a different direction, I remain a strong advocate for ${co} and the work your team is doing.

I would sincerely appreciate being kept in the loop for future positions that align with my background. I am committed to finding the right fit at ${co} and am willing to be patient.${contextSentence}

Thank you for your consideration, and I wish the team continued success.

Sincerely,
[Your Name]`,

`Dear ${name},

I received your update regarding the ${job} position and wanted to respond with gratitude. The process was a positive experience, and it only strengthened my desire to work at ${co}.

If I may be candid, I intend to continue pursuing opportunities with your organization. I believe the right role will come along, and I want to be ready for it. I hope you will consider me again when the time is right.${contextSentence}

Thank you for everything. I look forward to the possibility of working together in the future.

With appreciation,
[Your Name]`,
    ];
  }

  // Fallback (should never reach here)
  return [
`Dear ${name},

${opener} I wanted to reach out regarding the ${job} opportunity at ${co}. I remain very interested and would appreciate any updates.${contextSentence}

Thank you for your time.

Best regards,
[Your Name]`,
  ];
}

// ---------------------------------------------------------------------------
// Generate function
// ---------------------------------------------------------------------------

function generateEmail(
  scenario: Scenario,
  tone: Tone,
  timing: Timing,
  contactName: string,
  contactTitle: string,
  company: string,
  jobTitle: string,
  additionalContext: string,
  variationIndex?: number
): GeneratedEmail {
  const subjects = getSubjectLines(scenario, tone, jobTitle, company);
  const bodies = getBodyTemplates(
    scenario, tone, timing, contactName, contactTitle, company, jobTitle, additionalContext
  );

  const maxVariations = Math.min(subjects.length, bodies.length);
  const idx = variationIndex !== undefined ? variationIndex % maxVariations : Math.floor(Math.random() * maxVariations);

  return {
    subject: subjects[idx],
    body: bodies[idx],
    variationIndex: idx,
  };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function FollowUpEmailContent() {
  const [scenario, setScenario] = useState<Scenario>('After Interview');
  const [contactName, setContactName] = useState('');
  const [contactTitle, setContactTitle] = useState('');
  const [company, setCompany] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [timing, setTiming] = useState<Timing>('3-5 days');
  const [tone, setTone] = useState<Tone>('Professional');
  const [additionalContext, setAdditionalContext] = useState('');
  const [email, setEmail] = useState<GeneratedEmail | null>(null);
  const [generated, setGenerated] = useState(false);

  const handleGenerate = () => {
    const result = generateEmail(
      scenario, tone, timing, contactName, contactTitle, company, jobTitle, additionalContext
    );
    setEmail(result);
    setGenerated(true);
  };

  const handleRegenerate = () => {
    if (!email) return;
    const subjects = getSubjectLines(scenario, tone, jobTitle, company);
    const bodies = getBodyTemplates(scenario, tone, timing, contactName, contactTitle, company, jobTitle, additionalContext);
    const maxVariations = Math.min(subjects.length, bodies.length);
    let newIdx = Math.floor(Math.random() * maxVariations);
    while (newIdx === email.variationIndex && maxVariations > 1) {
      newIdx = Math.floor(Math.random() * maxVariations);
    }
    const result = generateEmail(
      scenario, tone, timing, contactName, contactTitle, company, jobTitle, additionalContext, newIdx
    );
    setEmail(result);
  };

  const handleToneChange = (newTone: Tone) => {
    setTone(newTone);
    if (generated) {
      const result = generateEmail(
        scenario, newTone, timing, contactName, contactTitle, company, jobTitle, additionalContext
      );
      setEmail(result);
    }
  };

  const fullEmailText = email ? `Subject: ${email.subject}\n\n${email.body}` : '';

  const inputClass =
    'w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-shadow text-gray-900';
  const labelClass = 'block text-sm font-semibold text-gray-700 mb-1.5';

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-stone-100">
      <ToolHero
        title="Follow-Up Email Generator"
        description="Generate professional follow-up emails for any job search scenario. After interviews, applications, networking events, and more."
        category="Job Search Tools"
      />

      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Input Form */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Follow-Up Details</h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {/* Scenario */}
            <div className="sm:col-span-2">
              <label htmlFor="scenario" className={labelClass}>
                Scenario
              </label>
              <select
                id="scenario"
                value={scenario}
                onChange={(e) => setScenario(e.target.value as Scenario)}
                className={inputClass}
              >
                {scenarios.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>

            {/* Contact Name */}
            <div>
              <label htmlFor="contactName" className={labelClass}>
                Contact Name
              </label>
              <input
                id="contactName"
                type="text"
                placeholder="Sarah Johnson"
                value={contactName}
                onChange={(e) => setContactName(e.target.value)}
                className={inputClass}
              />
            </div>

            {/* Contact Title */}
            <div>
              <label htmlFor="contactTitle" className={labelClass}>
                Their Title / Role
              </label>
              <input
                id="contactTitle"
                type="text"
                placeholder="Hiring Manager"
                value={contactTitle}
                onChange={(e) => setContactTitle(e.target.value)}
                className={inputClass}
              />
            </div>

            {/* Company Name */}
            <div>
              <label htmlFor="company" className={labelClass}>
                Company Name
              </label>
              <input
                id="company"
                type="text"
                placeholder="Acme Corp"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                className={inputClass}
              />
            </div>

            {/* Job Title */}
            <div>
              <label htmlFor="jobTitle" className={labelClass}>
                Job Title{' '}
                <span className="font-normal text-gray-400">(if applicable)</span>
              </label>
              <input
                id="jobTitle"
                type="text"
                placeholder="Senior Software Engineer"
                value={jobTitle}
                onChange={(e) => setJobTitle(e.target.value)}
                className={inputClass}
              />
            </div>

            {/* Days Since Last Contact */}
            <div>
              <label htmlFor="timing" className={labelClass}>
                Days Since Last Contact
              </label>
              <select
                id="timing"
                value={timing}
                onChange={(e) => setTiming(e.target.value as Timing)}
                className={inputClass}
              >
                {timings.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>

            {/* Tone */}
            <div>
              <label htmlFor="tone" className={labelClass}>
                Tone
              </label>
              <select
                id="tone"
                value={tone}
                onChange={(e) => handleToneChange(e.target.value as Tone)}
                className={inputClass}
              >
                {tones.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>

            {/* Additional Context */}
            <div className="sm:col-span-2">
              <label htmlFor="additionalContext" className={labelClass}>
                Additional Context{' '}
                <span className="font-normal text-gray-400">(optional -- adds a personal touch)</span>
              </label>
              <textarea
                id="additionalContext"
                rows={3}
                placeholder="e.g., We discussed the upcoming product launch, I mentioned my experience with cloud migrations, they seemed interested in my project management background"
                value={additionalContext}
                onChange={(e) => setAdditionalContext(e.target.value)}
                className={inputClass}
              />
            </div>
          </div>

          {/* Generate Button */}
          <div className="mt-8">
            <button
              onClick={handleGenerate}
              className="w-full sm:w-auto bg-gradient-to-r from-blue-600 to-blue-500 text-white font-bold py-3 px-10 rounded-xl shadow-lg hover:from-blue-700 hover:to-blue-600 transition-all text-lg"
            >
              Generate Follow-Up
            </button>
          </div>
        </div>

        {/* Results Section */}
        {generated && email && (
          <div className="mt-10">
            {/* Tone Tabs + Actions */}
            <div className="flex flex-wrap items-center gap-2 mb-6">
              {tones.map((t) => (
                <button
                  key={t}
                  onClick={() => handleToneChange(t)}
                  className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors ${
                    tone === t
                      ? 'bg-blue-600 text-white shadow'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {t}
                </button>
              ))}
              <div className="ml-auto flex items-center gap-2">
                <button
                  onClick={handleRegenerate}
                  className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-1.5 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                >
                  <RefreshCw className="w-3.5 h-3.5" />
                  Regenerate
                </button>
                <CopyButton text={fullEmailText} label="Copy All" />
              </div>
            </div>

            {/* Email Preview Card */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
              {/* Subject Line */}
              <div className="border-b border-gray-100 px-8 py-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-1">Subject Line</p>
                    <p className="text-lg font-semibold text-gray-900">{email.subject}</p>
                  </div>
                  <CopyButton text={email.subject} label="Copy" className="shrink-0 mt-1" />
                </div>
              </div>

              {/* Email Body */}
              <div className="px-8 py-6 sm:px-12 sm:py-8">
                <div className="flex items-center justify-between mb-4">
                  <p className="text-xs font-semibold uppercase tracking-wider text-gray-400">Email Body</p>
                  <CopyButton text={email.body} label="Copy Body" />
                </div>
                <div className="font-serif text-gray-800 leading-relaxed whitespace-pre-line text-base sm:text-lg">
                  {email.body}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tips Section */}
        <div className="mt-12 bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Follow-Up Email Etiquette</h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="flex gap-4">
              <div className="shrink-0 w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                <Clock className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Timing Matters</h3>
                <p className="text-sm text-gray-600">
                  For post-interview follow-ups, send within 24-48 hours. For application check-ins, wait at least one to two weeks. For networking contacts, follow up within two to three days while the conversation is fresh.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="shrink-0 w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Add Value Each Time</h3>
                <p className="text-sm text-gray-600">
                  Each follow-up should offer something new -- a relevant article, an update on your skills, or a fresh perspective on the role. Avoid simply repeating the same message. Show you are actively engaged and growing.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="shrink-0 w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Keep It Short</h3>
                <p className="text-sm text-gray-600">
                  Follow-up emails should be concise -- aim for 100 to 200 words. Hiring managers and contacts are busy. Get to the point, reiterate your interest, and make it easy for them to respond.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="shrink-0 w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                <Mail className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Frequency Guidelines</h3>
                <p className="text-sm text-gray-600">
                  After an interview, one follow-up is usually enough unless they gave you a specific timeline. For applications with no response, two to three follow-ups spaced one to two weeks apart is reasonable. Never follow up more than once per week.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="shrink-0 w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                <Ban className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Know When to Stop</h3>
                <p className="text-sm text-gray-600">
                  If you have sent two to three follow-ups with no response, it is time to move on. Continuing to send messages after that point can come across as pushy. Redirect your energy to other opportunities and circle back in a few months if appropriate.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="shrink-0 w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Proofread and Personalize</h3>
                <p className="text-sm text-gray-600">
                  Double-check names, titles, and company details before sending. A small typo can undermine your professionalism. Personalize each email with specific references to your conversation or the role -- generic messages are easy to ignore.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Educational Content */}
        <section className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 mt-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">The Art of the Job Search Follow-Up</h3>
          <div className="space-y-4 text-gray-700 leading-relaxed">
            <p>
              Following up during a job search is one of the most underused strategies available to candidates. Research consistently shows that <a href="https://www.indeed.com/career-advice/interviewing/how-to-follow-up-on-a-job-application" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">the majority of job applicants never follow up at all</a>, which means a well-timed, professional follow-up email can immediately set you apart from the competition. It signals genuine interest, persistence, and the kind of proactive communication skills that employers value in every role.
            </p>
            <p>
              Timing is everything when it comes to follow-ups. After a job interview, the standard best practice is to send a thank you email within 24 hours and a status follow-up one to two weeks later if you have not heard back. For applications where you have received no response, waiting at least seven to ten business days before your first follow-up shows patience while still demonstrating interest. Career experts at Harvard Business Review recommend limiting yourself to two or three follow-up attempts total, spaced about a week apart, before redirecting your energy to other opportunities.
            </p>
            <p>
              The most effective follow-up emails share a few key traits: they are brief (under 150 words), they reference a specific conversation or application detail, and they add something new rather than simply repeating the original message. This might be a link to a relevant article, a brief update on a new certification or project, or a fresh angle on why you are excited about the role. Each follow-up should make it easy for the recipient to respond, ideally with a simple yes or no to a specific question.
            </p>
            <h4 className="text-lg font-bold text-gray-900 pt-2">When to Follow Up by Scenario</h4>
            <ul className="list-disc pl-5 space-y-2">
              <li><strong>After an interview:</strong> Send a thank you within 24 hours. If you do not hear back within the timeline they provided, follow up once more after one to two business days past the deadline.</li>
              <li><strong>After submitting an application:</strong> Wait seven to ten business days, then send a brief, polite inquiry confirming receipt and expressing continued interest.</li>
              <li><strong>After a networking event or referral:</strong> Follow up within two to three days while the connection is still fresh. Reference something specific from your conversation.</li>
              <li><strong>After a rejection:</strong> Send a gracious note thanking them for the opportunity and expressing interest in future openings. This keeps the door open and leaves a lasting positive impression.</li>
            </ul>
            <p className="mt-6 pt-4 border-t border-gray-200 text-sm text-gray-500">
              <strong>Sources &amp; Further Reading:</strong>{' '}
              <a href="https://www.indeed.com/career-advice/interviewing/how-to-follow-up-on-a-job-application" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">Indeed: How to Follow Up on a Job Application</a>
              {' · '}
              <a href="https://hbr.org/2023/02/how-to-follow-up-on-a-job-application-the-right-way" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">Harvard Business Review: Following Up the Right Way</a>
              {' · '}
              <a href="https://www.thebalancemoney.com/how-to-follow-up-after-submitting-a-resume-2063961" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">The Balance: How to Follow Up After Submitting a Resume</a>
            </p>
          </div>
        </section>

        {/* CTA */}
        <ToolCTA
          headline="Ready to land more interviews?"
          description="Use our AI-powered resume builder to craft a polished, ATS-friendly resume that gets you noticed by recruiters and hiring managers."
          buttonText="Build My Resume Free"
          buttonHref="https://app.superpowerresume.com/sign-up"
        />
      </div>
    </div>
  );
}
